import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';

import { Select } from '@/shared/ui/Input';
import { AlertIcon, FolderIcon, InboxIcon, MailIcon, TrashIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';

import type { FolderCount, InboxView, InboxViews, ViewCount } from './types';
import { type InboxPlace, placeLabel, placeSearch } from './views';

/**
 * The folders of the inbox (contract v10.25): «Por revisar» on top, where the inbox
 * opens; the mailbox's folders as «Correcto» files the mail, with the same names
 * Thunderbird shows; then «Todos», the trash, the server's spam folder and, only when
 * there is any, «Con error».
 *
 * A column of links on a desktop and one select («Ver») on a phone. Every entry is an
 * address (`?vista=`, `?carpeta=`), so the browser's back walks the places a person
 * looked at.
 */

type Entry = {
  place: InboxPlace;
  icon: ReactNode;
  /** The number beside the name: what is left in «Por revisar», unread elsewhere. */
  count: number | undefined;
  /** Painted as a pill: something is waiting. */
  loud: boolean;
  tone?: 'danger';
};

function viewCount(data: InboxViews | undefined, view: InboxView): ViewCount | undefined {
  return data?.views?.find((each) => each.view === view);
}

function entriesOf(data: InboxViews | undefined): {
  top: Entry[];
  folders: Entry[];
  rest: Entry[];
} {
  const pending = viewCount(data, 'por_revisar');
  const all = viewCount(data, 'todos');
  const trash = viewCount(data, 'papelera');
  const spam = viewCount(data, 'spam');
  const errors = viewCount(data, 'errores');
  const top: Entry[] = [
    {
      place: { view: 'por_revisar', folder: null },
      icon: <InboxIcon size={16} />,
      count: pending?.total,
      loud: (pending?.total ?? 0) > 0,
    },
  ];
  const folders: Entry[] = (data?.folders ?? []).map((folder: FolderCount) => ({
    place: { view: 'carpeta', folder: folder.folder },
    icon: <FolderIcon size={16} />,
    count: folder.unread || undefined,
    loud: folder.unread > 0,
  }));
  const rest: Entry[] = [
    {
      place: { view: 'todos', folder: null },
      icon: <MailIcon size={16} />,
      count: all?.unread || undefined,
      loud: false,
    },
    {
      place: { view: 'papelera', folder: null },
      icon: <TrashIcon size={16} />,
      count: trash?.total || undefined,
      loud: false,
    },
    {
      place: { view: 'spam', folder: null },
      icon: <AlertIcon size={16} />,
      count: spam?.total || undefined,
      loud: false,
    },
  ];
  // «Con error» only while there is one: an empty view nobody needs is noise on the rail.
  if ((errors?.total ?? 0) > 0) {
    rest.push({
      place: { view: 'errores', folder: null },
      icon: <AlertIcon size={16} />,
      count: errors?.total,
      loud: true,
      tone: 'danger',
    });
  }
  return { top, folders, rest };
}

function samePlace(a: InboxPlace, b: InboxPlace): boolean {
  return a.view === b.view && (a.view !== 'carpeta' || a.folder === b.folder);
}

function RailLink({
  entry,
  current,
  params,
}: {
  entry: Entry;
  current: InboxPlace;
  params: URLSearchParams;
}) {
  const active = samePlace(entry.place, current);
  const search = placeSearch(entry.place, params);
  return (
    <li>
      <Link
        to={search ? `/correos?${search}` : '/correos'}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[14px] transition-colors',
          'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
          active
            ? 'bg-card text-ink shadow-card font-medium'
            : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
        )}
      >
        <span className={cn('shrink-0', active ? 'text-accent' : 'text-ink-4')}>{entry.icon}</span>
        <span className="min-w-0 flex-1 truncate">{placeLabel(entry.place)}</span>
        {entry.count !== undefined ? (
          <span
            className={cn(
              'shrink-0 rounded-full px-1.5 text-[12px] tabular-nums',
              entry.tone === 'danger'
                ? 'bg-danger-soft text-danger'
                : entry.loud
                  ? 'bg-info-soft text-info font-semibold'
                  : 'text-ink-4',
            )}
          >
            {entry.count}
          </span>
        ) : null}
      </Link>
    </li>
  );
}

/** The column of a desktop. */
export function InboxRail({
  data,
  current,
  params,
}: {
  data: InboxViews | undefined;
  current: InboxPlace;
  params: URLSearchParams;
}) {
  const { top, folders, rest } = entriesOf(data);
  return (
    // From `xl`: below 1280 px the rail would leave the subject a few words, and the
    // select «Ver» does the same job.
    <nav aria-label="Carpetas" className="hidden flex-col gap-4 xl:sticky xl:top-6 xl:flex">
      <ul className="flex flex-col gap-0.5">
        {top.map((entry) => (
          <RailLink key="top" entry={entry} current={current} params={params} />
        ))}
      </ul>
      {folders.length > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="eyebrow px-2.5">Carpetas</p>
          <ul className="flex flex-col gap-0.5">
            {folders.map((entry) => (
              <RailLink key={entry.place.folder} entry={entry} current={current} params={params} />
            ))}
          </ul>
        </div>
      ) : null}
      <ul className="border-line-2 flex flex-col gap-0.5 border-t pt-3">
        {rest.map((entry) => (
          <RailLink key={entry.place.view} entry={entry} current={current} params={params} />
        ))}
      </ul>
    </nav>
  );
}

function optionValue(place: InboxPlace): string {
  return place.view === 'carpeta' ? `carpeta:${place.folder ?? ''}` : place.view;
}

/** «Por revisar (12)», «Pedidos cliente (2 sin leer)»: what the number means differs,
 * and in a select there is no pill to say it. */
function optionLabel(entry: Entry): string {
  const name = placeLabel(entry.place);
  if (entry.count === undefined) return name;
  const unread = entry.place.view === 'carpeta' || entry.place.view === 'todos';
  return unread ? `${name} (${entry.count} sin leer)` : `${name} (${entry.count})`;
}

/** The same places as one select, for a phone: «Ver: Por revisar (12)». */
export function InboxPicker({
  data,
  current,
  params,
}: {
  data: InboxViews | undefined;
  current: InboxPlace;
  params: URLSearchParams;
}) {
  const navigate = useNavigate();
  const { top, folders, rest } = entriesOf(data);
  const all = [...top, ...folders, ...rest];
  // The folder in the address may not be on the rail (renamed in the panel meanwhile):
  // it is still an option, or the select would say something it is not showing.
  const known = all.some((entry) => samePlace(entry.place, current));
  function choose(value: string): void {
    const place: InboxPlace = value.startsWith('carpeta:')
      ? { view: 'carpeta', folder: value.slice('carpeta:'.length) }
      : { view: value as InboxView, folder: null };
    const search = placeSearch(place, params);
    void navigate(search ? `/correos?${search}` : '/correos');
  }
  return (
    <div className="xl:hidden">
      <Select
        label="Ver"
        value={optionValue(current)}
        onChange={(event) => choose(event.target.value)}
      >
        {top.map((entry) => (
          <option key="top" value={optionValue(entry.place)}>
            {optionLabel(entry)}
          </option>
        ))}
        {folders.length > 0 ? (
          <optgroup label="Carpetas">
            {folders.map((entry) => (
              <option key={entry.place.folder} value={optionValue(entry.place)}>
                {optionLabel(entry)}
              </option>
            ))}
          </optgroup>
        ) : null}
        {rest.map((entry) => (
          <option key={entry.place.view} value={optionValue(entry.place)}>
            {optionLabel(entry)}
          </option>
        ))}
        {known ? null : <option value={optionValue(current)}>{placeLabel(current)}</option>}
      </Select>
    </div>
  );
}
